import { theme } from '@gympass/yoga';
import { useQuery } from '@tanstack/react-query';
import withAuth from 'hocs/withAuth/withAuth';
import { FormattedMessage } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';

import { useDateUtils } from 'hooks';
import { useLogger } from 'hooks/useLogger/useLogger';
import {
  getEndUserInfo,
  ScoreType,
  getEndUserAssessmentAnswersV2,
} from 'services';

import {
  HeaderUserInfo,
  Loading,
  TabList,
  ClinicalAssessmentAnswersTable,
  Card,
} from 'components';

import {
  ClinicalAssessmentAnswersRouteParams,
  ClinicalAssessmentAnswersRouteState,
} from './types';

import * as S from './styles';

const ClinicalAssessmentAnswers: React.FC = ({ children }) => {
  const { id = '', submissionId = '' } =
    useParams<ClinicalAssessmentAnswersRouteParams>();
  const {
    state: { scoreType, date },
  }: {
    state: ClinicalAssessmentAnswersRouteState;
  } = useLocation();
  const { toLongDate } = useDateUtils();
  const logger = useLogger();

  const { data: userInfo, isLoading: isLoadingUserInfo } = useQuery(
    ['endUserInfo', id],
    () => getEndUserInfo(id),
    {
      onError: e => logger.error('Error fetching user info', { e }),
    },
  );

  const {
    data: responseAnswers,
    isError: isErrorAnswers,
    isLoading: isLoadingAnswers,
  } = useQuery(
    ['clinicalEndUserAssessmentAnswers', id],
    () => getEndUserAssessmentAnswersV2(id, submissionId, scoreType),
    {
      onError: e => logger.error('Error fetching user answers', { e }),
    },
  );

  const renderTitle = () => {
    if (scoreType === ScoreType.PHQ) {
      return (
        <>
          <FormattedMessage id="ENDUSER.CLINICALASSESSMENT.PHQ.LABEL" />
          {' - '}
          {toLongDate(new Date(date))}
        </>
      );
    }

    if (scoreType === ScoreType.WHO) {
      return (
        <>
          <FormattedMessage id="ENDUSER.CLINICALASSESSMENT.WHO.LABEL" />
          {' - '}
          {toLongDate(new Date(date))}
        </>
      );
    }

    return (
      <>
        <FormattedMessage id="ENDUSER.CLINICALASSESSMENT.GAD.LABEL" />
        {' - '}
        {toLongDate(new Date(date))}
      </>
    );
  };

  const renderAnswers = () => {
    if (isErrorAnswers) {
      return (
        <FormattedMessage id="PSYCHOLOGICALRECORD.SUBTITLE.GENERALERROR" />
      );
    }

    return (
      <ClinicalAssessmentAnswersTable
        data-testid="clinicalAssessmentAnswersTable"
        data={responseAnswers?.answers || []}
      />
    );
  };

  return (
    <S.UserInfoContainer data-testid="clinicalAssessmentAnswers">
      <Loading isLoading={isLoadingUserInfo || isLoadingAnswers} />
      <HeaderUserInfo user={userInfo?.data} />
      <S.Divisor />
      <S.TabsContainer data-testid="tabsContainer">
        <TabList selectedIndex={3} />
        <main>
          <S.CrumbContainer data-testid="clinicalAssessmentBreadcrumb">
            {children}
          </S.CrumbContainer>
          <S.AssessmentAnswers data-testid="clinicalAssessment">
            <Card padding={theme.spacing.medium}>
              <S.AssessmentTitle data-testid="clinicalAssessmentTitle">
                {renderTitle()}
              </S.AssessmentTitle>
              {!userInfo?.data?.care_profile &&
                responseAnswers?.answers?.[0]?.question && (
                  <S.AssessmentIntroduction data-testid="clinicalAssessmentIntroduction">
                    <FormattedMessage
                      id={`${responseAnswers?.answers?.[0]?.question}`}
                    />
                  </S.AssessmentIntroduction>
                )}
              {renderAnswers()}
            </Card>
          </S.AssessmentAnswers>
        </main>
      </S.TabsContainer>
    </S.UserInfoContainer>
  );
};

export default withAuth(ClinicalAssessmentAnswers);
